/**
 * useMessageLimitWarning - Manages long conversation warnings
 *
 * Shows a warning modal when a conversation exceeds 25 messages, suggesting
 * users start a new chat for better performance. Tracks dismissals with
 * a 5-minute expiry timer to avoid annoying users.
 *
 * @param {Object} params
 * @param {string|null} params.conversationId - Current conversation ID
 * @param {number} params.messageCount - Number of messages in conversation
 * @param {boolean} params.isStreaming - Whether messages are currently streaming
 * @param {boolean} params.isLoading - Whether the UI is loading
 * @param {Function} params.onNewChat - Callback when user clicks "Start New Chat"
 *
 * @returns {Object} Hook state and handlers
 * @returns {boolean} shouldShowWarning - Whether to display the warning modal
 * @returns {number} messageCount - The current message count
 * @returns {Function} handleContinue - User chooses to continue in current chat
 * @returns {Function} handleNewChat - User chooses to start a new chat
 * @returns {Function} handleClose - User closes the modal (same as continue)
 */

import { useState, useEffect, useCallback } from 'react';

// Constants
const MESSAGE_THRESHOLD = 25;
const DISMISSAL_EXPIRY_HOURS = 0.083; // ~5 minutes
const STORAGE_KEY = 'dismissedMessageLimitPrompts';

/**
 * Get dismissal data from sessionStorage
 * @returns {{conversations: string[], timestamp: number} | null}
 */
const getDismissalData = () => {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const parsed = JSON.parse(stored);

    // Migrate old array format to new object format
    if (Array.isArray(parsed)) {
      sessionStorage.removeItem(STORAGE_KEY);
      console.log('Migrated old dismissal format');
      return null;
    }

    // Validate format
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.conversations)) {
      return parsed;
    }

    return null;
  } catch (e) {
    sessionStorage.removeItem(STORAGE_KEY);
    console.log('Invalid dismissal data, cleared');
    return null;
  }
};

/**
 * Check if dismissal data has expired (>5 minutes old)
 * @param {number} timestamp - Dismissal timestamp
 * @returns {boolean}
 */
const isExpired = (timestamp) => {
  if (!timestamp) return true;
  const hoursAgo = (Date.now() - timestamp) / (1000 * 60 * 60);
  return hoursAgo > DISMISSAL_EXPIRY_HOURS;
};

/**
 * Add conversation to dismissed list
 * @param {string} conversationId
 */
const addToDismissedList = (conversationId) => {
  if (!conversationId) return;

  try {
    const dismissalData = getDismissalData() || { conversations: [], timestamp: Date.now() };

    // Don't add duplicates
    if (!dismissalData.conversations.includes(conversationId)) {
      dismissalData.conversations.push(conversationId);
      dismissalData.timestamp = Date.now();
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(dismissalData));
      console.log('Added to dismissed list:', conversationId);
    }
  } catch (e) {
    // Fallback - create new format
    const newData = {
      conversations: [conversationId],
      timestamp: Date.now()
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
    console.log('Created new dismissal tracking:', conversationId);
  }
};

export const useMessageLimitWarning = ({
  conversationId,
  messageCount,
  isStreaming,
  isLoading,
  onNewChat
}) => {
  const [shouldShowWarning, setShouldShowWarning] = useState(false);

  // Main visibility logic
  useEffect(() => {
    // Reset visibility on conversation change
    setShouldShowWarning(false);

    // Early returns for invalid states
    if (!conversationId) return;
    if (!messageCount || messageCount <= MESSAGE_THRESHOLD) return;
    if (isStreaming || isLoading) return;

    // Check dismissal status
    const dismissalData = getDismissalData();

    // If no dismissal data, show warning
    if (!dismissalData) {
      setShouldShowWarning(true);
      return;
    }

    // Check if expired
    if (isExpired(dismissalData.timestamp)) {
      sessionStorage.removeItem(STORAGE_KEY);
      console.log(`Dismissals expired after ${Math.round((Date.now() - dismissalData.timestamp) / 60000)} minutes`);
      setShouldShowWarning(true);
      return;
    }

    // Check if this conversation is dismissed
    const isDismissed = dismissalData.conversations.includes(conversationId);

    if (!isDismissed) {
      console.log('Message limit check:', {
        conversationId,
        messageCount,
        isDismissed,
        minutesUntilExpiry: Math.round((DISMISSAL_EXPIRY_HOURS * 60) - ((Date.now() - dismissalData.timestamp) / (1000 * 60)))
      });
      setShouldShowWarning(true);
    }
  }, [conversationId, messageCount, isStreaming, isLoading]);

  // Handler: User clicks "Continue Here"
  const handleContinue = useCallback(() => {
    setShouldShowWarning(false);
    addToDismissedList(conversationId);
  }, [conversationId]);

  // Handler: User clicks "Start New Chat"
  const handleNewChat = useCallback(() => {
    setShouldShowWarning(false);
    if (onNewChat) {
      onNewChat();
    }
  }, [onNewChat]);

  // Handler: User closes modal (X button)
  const handleClose = useCallback(() => {
    setShouldShowWarning(false);
    addToDismissedList(conversationId);
  }, [conversationId]);

  return {
    shouldShowWarning,
    messageCount,
    handleContinue,
    handleNewChat,
    handleClose
  };
};
